import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import React, { useCallback, useMemo, useRef } from 'react'
import OfferCapacityWithAgesVerticalSelection from './OfferCapacityWithAgesVerticalSelection'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { formToObject } from 'lib/forms/formToObject'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  offer: App.Offer
  onClose?: () => void;
  onApply?: () => void;
  footerText: string;
  disableMultiRoom?: boolean;
}

function OfferCapacityWithAgesVerticalSidebarSelection(props: Props) {
  const { offer, onClose = noop, onApply = noop, footerText, disableMultiRoom } = props

  const modalContext = useModalElementContext()
  const dispatch = useOfferPageDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  const saveRooms = useCallback(() => {
    if (formRef.current?.checkValidity()) {
      const values = formToObject<{ occupants: App.RoomOccupants}>(formRef.current)
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: [values.occupants],
      })
    }
  }, [dispatch])

  const onCloseClick = useCallback(() => {
    saveRooms()
    onClose()
    modalContext?.resolve()
  }, [modalContext, onClose, saveRooms])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveRooms()
    onApply()
    modalContext?.resolve()
  }

  return <form onSubmit={onSubmit} ref={formRef}>
    <ModalHeader
      title="Who is going?"
      subtitle={maxGuests > 0 ? `Maximum occupancy is ${pluralizeToString('guest', maxGuests)}` : undefined}
      onCloseButtonClick={onCloseClick}
    />
    <ModalBody>
      <ModalContent>
        <OfferCapacityWithAgesVerticalSelection
          capacities={capacities}
          disableMultiRoom={disableMultiRoom}
        />
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: footerText,
        type: 'submit',
      }}
    />
  </form>
}

export default withOfferPageState((state) => ({
  offer: state.offer,
}))(OfferCapacityWithAgesVerticalSidebarSelection)
